import React, { useState, useMemo } from 'react';
import {
  formatCurrency,
  getPaymentTypeIcon,
  getPaymentTypeColor
} from './kasaBankHelpers';
import { DateRangeFilter, Pagination } from './sharedControls';

/**
 * Reusable table for KP / KW document lists with payment type filter,
 * server-side date range filter and server-side pagination.
 *
 * @param {'KP'|'KW'} type            Document type
 * @param {Array}      documents      Bieząca strona dokumentow
 * @param {boolean}    canManage      Whether the user can add new docs
 * @param {Function}   onAddClick     Callback to open Add modal
 * @param {Object}     dateFilters    { startDate, endDate }
 * @param {Function}   setDateFilters Setter dla dateFilters (server-side)
 * @param {number}     page           Bieząca strona (1-based)
 * @param {number}     pageLimit      Ilosc wynikow na strone
 * @param {number}     totalCount     Calkowita liczba pasujacych dokumentow
 * @param {Function}   onPageChange   Setter dla page
 * @param {Function}   onLimitChange  Setter dla pageLimit
 * @param {boolean}    isLoading      Czy trwa fetch
 */
function DocumentsTable({
  type,
  documents,
  canManage,
  onAddClick,
  dateFilters = { startDate: '', endDate: '' },
  setDateFilters,
  page = 1,
  pageLimit = 50,
  totalCount = 0,
  onPageChange,
  onLimitChange,
  isLoading = false
}) {
  const [paymentFilter, setPaymentFilter] = useState('all');

  const isKP = type === 'KP';
  const accentColor = isKP ? 'var(--success)' : 'var(--danger)';
  const title = isKP ? 'Dokumenty KP (Kasa Przyjmie)' : 'Dokumenty KW (Kasa Wydaje)';
  const btnLabel = isKP ? 'Dodaj wpływ' : 'Dodaj wydatek';
  const emptyLabel = isKP ? 'Brak dokumentów KP do wyświetlenia' : 'Brak dokumentów KW do wyświetlenia';
  const iconClass = isKP ? 'fas fa-plus-circle' : 'fas fa-minus-circle';
  const btnIcon = isKP ? 'fas fa-plus' : 'fas fa-minus';
  const gradient = isKP
    ? 'linear-gradient(135deg, var(--success) 0%, var(--success) 100%)'
    : 'linear-gradient(135deg, var(--danger) 0%, var(--orange) 100%)';
  const shadowColor = isKP ? 'rgba(40, 167, 69, 0.2)' : 'rgba(220, 53, 69, 0.2)';
  const shadowColorHover = isKP ? 'rgba(40, 167, 69, 0.3)' : 'rgba(220, 53, 69, 0.3)';

  // Get unique payment types from documents (na bieżącej stronie)
  const paymentTypes = useMemo(() => {
    const types = new Set();
    (documents || []).forEach(doc => {
      if (doc.typ_platnosci) types.add(doc.typ_platnosci);
    });
    return Array.from(types).sort();
  }, [documents]);

  // Filter documents by payment type (client-side, w obrebie strony)
  const filteredDocuments = useMemo(() => {
    if (!documents) return [];
    if (paymentFilter === 'all') return documents;
    return documents.filter(doc => doc.typ_platnosci === paymentFilter);
  }, [documents, paymentFilter]);

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '0.6rem', flexWrap: 'wrap', gap: '0.5rem'
      }}>
        <h5 style={{ margin: 0, fontWeight: '600', color: 'var(--text)' }}>
          <i className={iconClass} style={{ color: accentColor, marginRight: '0.5rem' }}></i>
          {title} ({totalCount}{isLoading ? ' · ladowanie...' : ''})
        </h5>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {/* Payment type filter (na biezacej stronie) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Płatność (str.):</span>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              style={{
                padding: '6px 10px',
                fontSize: '12px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text)',
                cursor: 'pointer'
              }}
            >
              <option value="all">Wszystkie ({documents?.length || 0})</option>
              {paymentTypes.map(typ => (
                <option key={typ} value={typ}>
                  {typ} ({documents?.filter(d => d.typ_platnosci === typ).length || 0})
                </option>
              ))}
            </select>
          </div>

          {canManage && (
            <button
              onClick={onAddClick}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '6px 12px', background: gradient,
                color: '#fff' ,
                fontWeight: '500', fontSize: '12px', cursor: 'pointer',
                boxShadow: `0 2px 4px ${shadowColor}`, transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 8px ${shadowColorHover}`;
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 4px ${shadowColor}`;
              }}
            >
              <i className={btnIcon}></i>
              {btnLabel}
            </button>
          )}
        </div>
      </div>

      {/* Date range filter (server-side) */}
      {setDateFilters && (
        <DateRangeFilter dateFilters={dateFilters} setDateFilters={setDateFilters} />
      )}

      {/* Filter summary (payment - client-side) */}
      {paymentFilter !== 'all' && (
        <div style={{
          marginBottom: '0.5rem',
          padding: '0.4rem 0.75rem',
          backgroundColor: `${getPaymentTypeColor(paymentFilter)}15`,
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '0.75rem'
        }}>
          <i className={getPaymentTypeIcon(paymentFilter)} style={{ color: getPaymentTypeColor(paymentFilter) }}></i>
          <span>Filtr: <strong style={{ textTransform: 'capitalize' }}>{paymentFilter}</strong></span>
          <span style={{ color: 'var(--text-muted)' }}>
            ({filteredDocuments.length} z {documents?.length || 0} na stronie)
          </span>
          <button
            onClick={() => setPaymentFilter('all')}
            style={{
              marginLeft: 'auto',
              padding: '4px 10px',
              fontSize: '11px',
              backgroundColor: 'var(--border)',
              cursor: 'pointer'
            }}
          >
            Wyczyść
          </button>
        </div>
      )}

      {filteredDocuments.length > 0 ? (
        <div style={{ overflowX: 'auto', maxHeight: '350px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem', minWidth: '550px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Data', 'Nr dok.', 'Płatność', 'Kontrahent', 'Opis'].map((h) => (
                  <th key={h} style={{
                    padding: '0.3rem 0.2rem', textAlign: 'left',
                    fontWeight: '600', color: 'var(--text-secondary)', fontSize: '0.65rem'
                  }}>{h}</th>
                ))}
                <th style={{
                  padding: '0.3rem 0.2rem', textAlign: 'right',
                  fontWeight: '600', color: 'var(--text-secondary)', fontSize: '0.65rem'
                }}>Kwota</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map((doc, index) => (
                <tr key={doc.id || index} style={{ borderBottom: '1px solid var(--bg)' }}>
                  <td style={{ padding: '0.35rem 0.25rem' }}>{doc.data_operacji}</td>
                  <td style={{ padding: '0.35rem 0.25rem' }}>
                    <span style={{
                      fontFamily: 'monospace', fontSize: '0.8rem',
                      backgroundColor: 'var(--bg)', padding: '0.25rem 0.5rem', borderRadius: '0.25rem'
                    }}>
                      {doc.numer_dokumentu || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '0.35rem 0.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <i className={getPaymentTypeIcon(doc.typ_platnosci)} style={{ color: getPaymentTypeColor(doc.typ_platnosci) }}></i>
                      <span style={{ textTransform: 'capitalize' }}>{doc.typ_platnosci}</span>
                    </div>
                  </td>
                  <td style={{ padding: '0.35rem 0.25rem' }}>{doc.kontrahent || '-'}</td>
                  <td style={{ padding: '0.35rem 0.25rem' }}>{doc.opis || '-'}</td>
                  <td style={{
                    padding: '0.35rem 0.25rem', textAlign: 'right',
                    fontWeight: '600', color: accentColor
                  }}>
                    {isKP ? '+' : '-'}{formatCurrency(doc.kwota)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          <i className={`${iconClass} fa-3x`} style={{ marginBottom: '0.6rem', opacity: 0.3, color: accentColor }}></i>
          <p style={{ margin: 0 }}>{emptyLabel}</p>
        </div>
      )}

      {/* Pagination */}
      {totalCount > 0 && onPageChange && (
        <Pagination
          page={page}
          pageLimit={pageLimit}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onLimitChange={onLimitChange}
        />
      )}
    </div>
  );
}

export default DocumentsTable;
